import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Hero from './components/Hero';
import CategorySection from './components/CategorySection';
import { MovieGrid } from './components/MovieGrid';
import { hotList, newList, topRated } from '@/mocks/movies';

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Carousel */}
        <Hero />

        {/* Category Section */}
        <CategorySection />

        {/* Hot / Trending */}
        <MovieGrid
          title="热门推荐"
          icon="ri-fire-line"
          movies={hotList}
          viewAllLink="/category/dianying"
        />

        {/* New Arrivals */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12 bg-background-100/50">
          <MovieGrid
            title="最新上线"
            icon="ri-rocket-2-line"
            movies={newList}
            viewAllLink="/category/dianying"
          />
        </section>

        {/* Top Rated */}
        <MovieGrid
          title="高分必看"
          icon="ri-trophy-line"
          movies={topRated}
          viewAllLink="/category/dianying"
        />

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20">
          <div className="relative rounded-2xl overflow-hidden bg-background-100 border border-background-200/40">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Cinematic%20home%20theater%20setup%20with%20warm%20ambient%20lighting%2C%20large%20screen%20displaying%20movie%20scene%2C%20cozy%20atmosphere%2C%20dark%20background%20with%20gold%20accents%2C%20cinematic%20aesthetic%2C%20sophisticated%20home%20entertainment%2C%20no%20text&width=1600&height=400&seq=cta-bg-home-01&orientation=landscape"
                alt="畅享观影体验"
                className="w-full h-full object-cover opacity-30"
              />
            </div>
            <div className="relative z-10 py-12 md:py-16 px-6 md:px-12 text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-100 font-heading mb-3">
                海量影视资源，尽在大师兄影视
              </h2>
              <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-2xl mx-auto">
                涵盖电影、电视剧、动漫、综艺、纪录片等多种类型，免费在线观看，高清不卡顿，秒开播放，畅享流畅观影体验
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="/category/dianying"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  开始观影
                </a>
                <a
                  href="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-200/60 hover:bg-background-300/60 text-foreground-200 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: 'ri-4k-line', title: '高清画质', desc: '支持4K超清播放，还原影院级视觉体验' },
              { icon: 'ri-flashlight-line', title: '秒开播放', desc: '极速加载技术，点击即播无需等待' },
              { icon: 'ri-stack-line', title: '海量资源', desc: '覆盖电影、电视剧、动漫、综艺等各类内容' },
              { icon: 'ri-shield-check-line', title: '纯净观影', desc: '无广告打扰，沉浸式观影体验' },
            ].map((feature) => (
              <div
                key={feature.title}
                className="flex flex-col items-center text-center p-6 rounded-xl bg-background-100 border border-background-200/40"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl bg-primary-500/10 text-primary-400 mb-4">
                  <i className={`${feature.icon} text-xl md:text-2xl`}></i>
                </div>
                <h3 className="text-sm md:text-base font-semibold text-foreground-200 mb-2 font-heading">
                  {feature.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '大师兄影视是否需要注册？', a: '完全不需要注册！打开网站即可免费观看所有影视内容，无需任何账号或登录操作。' },
                { q: '观看视频需要付费吗？', a: '不需要。大师兄影视所有内容均为免费观看，无任何隐藏收费项目。' },
                { q: '支持哪些设备观看？', a: '支持电脑、平板、手机等所有主流设备，打开浏览器即可畅享高清观影体验。' },
                { q: '视频画质如何？', a: '我们提供高清甚至4K超清画质选择，根据您的网络环境自动适配最佳清晰度，确保流畅不卡顿。' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 border border-background-200/40 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer text-sm md:text-base text-foreground-200 font-medium">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: '大师兄影视',
              url: 'https://www.dashixiong.com/',
              description: '大师兄影视提供海量免费电影、电视剧在线观看，高清画质不卡顿，全集播放，最新影视热播推荐，秒开播放极速加载，畅享流畅观影体验。',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.dashixiong.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '大师兄影视',
              url: 'https://www.dashixiong.com/',
              description: '大师兄影视 - 免费电影在线看剧高清视频不卡顿全集播放平台',
              contactPoint: {
                '@type': 'ContactPoint',
                email: 'contact@dashixiong.com',
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '大师兄影视是否需要注册？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '完全不需要注册！打开网站即可免费观看所有影视内容，无需任何账号或登录操作。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '观看视频需要付费吗？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '不需要。大师兄影视所有内容均为免费观看，无任何隐藏收费项目。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '支持哪些设备观看？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '支持电脑、平板、手机等所有主流设备，打开浏览器即可畅享高清观影体验。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '视频画质如何？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '我们提供高清甚至4K超清画质选择，根据您的网络环境自动适配最佳清晰度，确保流畅不卡顿。',
                  },
                },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}